<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import IndividualAccountLayout from '@/modules/investing/components/individuals/individual-account-layout.vue'
import IndividualLayout from '@/modules/investing/components/individuals/individual-layout.vue'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import { VButton, VIcon, VTable } from '@/modules/shared/components'
import VSelect from '@/modules/investing/components/v-select.vue'

const individualStore = useIndividualStore()

///////////////////////////////////////////////////////////////////////////////
// Calls
///////////////////////////////////////////////////////////////////////////////

const permissions = computed(() => individualStore.listRoles)

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const { individual_id, slug } = useRoute().params as { individual_id: string; slug: string }

const fetch = async () => {
  await individualStore.fetchRoles(individual_id, { slug })
}

const changeRole = async (role_id, role) => {
  const payload = {
    role_id,
    role_name: role,
  }
  await individualStore.updateRole(role_id, payload, { slug })
  await fetch()
}

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const authStore = useAuthStore()
const isSiteAdmin = computed(() => authStore.is_site_admin)
const isGroupAdmin = computed(() => authStore.is_group_admin)
const canChangeRole = (group_slug) => isSiteAdmin || (isGroupAdmin && group_slug === slug)

const roleOptions = computed(() => {
  if (isSiteAdmin.value) {
    return [
      { value: 'Site Admin', label: 'Site Admin' },
      { value: 'Group Admin', label: 'Group Admin' },
      { value: 'User', label: 'User' },
    ]
  } else if (isGroupAdmin.value) {
    return [
      { value: 'Group Admin', label: 'Group Admin' },
      { value: 'User', label: 'User' },
    ]
  } else {
    return [{ value: 'User', label: 'User' }]
  }
})

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()
onMounted(async () => {
  showSkeleton()
  await individualStore.fetchIndividual(individual_id, { slug })
  await fetch()
  hideSkeleton()
})
</script>

<template>
  <IndividualLayout selectedTab="account">
    <IndividualAccountLayout selectedTab="permissions">
      <VTable
        :columns="[
          {
            key: 'group_name',
            name: 'Group',
            sorted: true,
            type: 'string',
            align: 'left',
            fixed: true,
            is_visible: true,
          },
          {
            key: 'actions',
            name: 'Role',
            type: 'actions',
            align: 'left',
            is_visible: true,
          },
        ]"
        :items="permissions"
        :skeleton="skeleton"
        name="individual-permissions"
        :slots="['actions']"
      >
        <template #actions="{ item }">
          <div class="flex space-x-1">
            <select
              class="mt-1 block h-8 w-full rounded-md border border-gray-300 text-xs shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
              :value="item.role_name"
              @change="(e) => changeRole(item.id, e.target.value)"
              v-if="canChangeRole(item.group_slug)"
            >
              <option v-for="option in roleOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
        </template>
      </VTable>
    </IndividualAccountLayout>
  </IndividualLayout>
</template>
